// src/features/organisation/components/employees/MultiSiteManager.tsx
import React, { useState, useEffect } from 'react';
import { Employee, MultiSiteAssignment } from '../../../../types/organisation/employees.types';
import { useSites } from '../../hooks/useSites';
import { useAssignmentTypes } from '../../hooks/useReferenceData';
import { apiClient } from '../../../../services/api/client';
import {
  Plus,
  Trash2,
  Globe,
  Building2
} from 'lucide-react';

interface MultiSiteManagerProps {
  employee: Employee;
  onUpdate?: (employee: Employee) => void;
  onMultiSiteChange?: (change: PendingMultiSiteChange) => void;
  pendingMultiSiteChanges?: PendingMultiSiteChange[];
}

interface NewMultiSite {
  siteId: number;
  role: string;
  assignmentType: string;
  effectiveFrom: string;
  effectiveTo?: string;
}

// Utilisation du type importé MultiSiteAssignment

interface PendingMultiSiteChange {
  type: 'add' | 'remove' | 'modify';
  id?: number;
  data?: {
    siteId: number;
    role: string;
    assignmentType: string;
    effectiveFrom: string;
    effectiveTo?: string;
  };
}

const MultiSiteManager: React.FC<MultiSiteManagerProps> = ({
  employee,
  onMultiSiteChange,
  pendingMultiSiteChanges = []
}) => {
  const { sites } = useSites();
  const { assignmentTypes } = useAssignmentTypes();

  // État local pour l'employé
  const [currentEmployee, setCurrentEmployee] = useState<Employee>(employee);

  const [showAddSite, setShowAddSite] = useState(false);
  const [editingSiteId, setEditingSiteId] = useState<number | null>(null);
  const [newSiteData, setNewSiteData] = useState<NewMultiSite>({
    siteId: 0,
    role: '',
    assignmentType: '',
    effectiveFrom: '',
    effectiveTo: ''
  });
  const [editingSiteData, setEditingSiteData] = useState<NewMultiSite>({
    siteId: 0,
    role: '',
    assignmentType: '',
    effectiveFrom: '',
    effectiveTo: ''
  });

  // Synchroniser avec l'employé parent
  useEffect(() => {
    setCurrentEmployee(employee);
  }, [employee]);

  // État local pour les sites multi-sites
  const [multiSiteAssignments, setMultiSiteAssignments] = useState<MultiSiteAssignment[]>([]);

  // Charger les sites multi-sites depuis l'API
  useEffect(() => {
    const fetchMultiSiteAssignments = async () => {
      try {
        const realAssignments = await apiClient.get(`/organisation/employees/${currentEmployee.id}/multi-site-assignments`);
        
        if (realAssignments && Array.isArray(realAssignments) && realAssignments.length > 0) {
          setMultiSiteAssignments(realAssignments);
          setCurrentEmployee(prev => ({
            ...prev,
            multiSiteAssignments: realAssignments
          }));
          return;
        }
      } catch (error) {
      }
      
      // Si pas de données réelles, utiliser les données de l'employé
      if (currentEmployee.multiSiteAssignments) {
        setMultiSiteAssignments(currentEmployee.multiSiteAssignments);
      }
    };

    if (currentEmployee?.id) {
      fetchMultiSiteAssignments();
    }
  }, [currentEmployee?.id]);


  const handleAddSite = () => {
    if (!newSiteData.siteId || !newSiteData.role || !newSiteData.assignmentType || !newSiteData.effectiveFrom) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const pendingChange: PendingMultiSiteChange = {
      type: 'add',
      data: {
        siteId: newSiteData.siteId,
        role: newSiteData.role,
        assignmentType: newSiteData.assignmentType,
        effectiveFrom: newSiteData.effectiveFrom,
        effectiveTo: newSiteData.effectiveTo
      }
    };

    // Notifier le parent avec le changement d'ajout
    if (onMultiSiteChange) {
      onMultiSiteChange(pendingChange);
    }

    // Mettre à jour l'affichage local
    const selectedSite = sites.find(s => s.id === newSiteData.siteId);
    const newAssignment: MultiSiteAssignment = {
      id: Date.now(),
      employeeId: currentEmployee.id || 0,
      siteId: newSiteData.siteId,
      siteName: selectedSite ? selectedSite.siteName : 'Site inconnu',
      siteCode: selectedSite ? selectedSite.siteCode : 'UNK',
      role: newSiteData.role,
      assignmentType: newSiteData.assignmentType,
      effectiveFrom: newSiteData.effectiveFrom,
      effectiveTo: newSiteData.effectiveTo,
      isActive: true,
      tenantId: 'demo' // Valeur par défaut
    };

    // Mettre à jour l'état local
    setMultiSiteAssignments(prev => [...prev, newAssignment]);
    
    setCurrentEmployee(prev => ({
      ...prev,
      multiSiteAssignments: [...(prev.multiSiteAssignments || []), newAssignment]
    }));

    // Réinitialiser le formulaire
    setNewSiteData({
      siteId: 0,
      role: '',
      assignmentType: '',
      effectiveFrom: '',
      effectiveTo: ''
    });
    setShowAddSite(false);
  };

  const handleEditSite = (assignmentId: number) => {
    const assignment = multiSiteAssignments.find(a => a.id === assignmentId);
    if (assignment) {
      setEditingSiteId(assignmentId);
      setEditingSiteData({
        siteId: assignment.siteId || 0,
        role: assignment.role,
        assignmentType: assignment.assignmentType,
        effectiveFrom: assignment.effectiveFrom,
        effectiveTo: assignment.effectiveTo || ''
      });
    }
  };

  const handleSaveSiteEdit = () => {
    if (!editingSiteData.siteId || !editingSiteData.role || !editingSiteData.assignmentType || !editingSiteData.effectiveFrom) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const pendingChange: PendingMultiSiteChange = {
      type: 'modify',
      id: editingSiteId!,
      data: {
        siteId: editingSiteData.siteId,
        role: editingSiteData.role,
        assignmentType: editingSiteData.assignmentType,
        effectiveFrom: editingSiteData.effectiveFrom,
        effectiveTo: editingSiteData.effectiveTo
      }
    };

    // Notifier le parent avec le changement de modification
    if (onMultiSiteChange) {
      onMultiSiteChange(pendingChange);
    }

    // Mettre à jour l'état local
    setMultiSiteAssignments(prev => prev.map(assignment => {
      if (assignment.id === editingSiteId) {
        const selectedSite = sites.find(s => s.id === editingSiteData.siteId);
        const updatedAssignment: MultiSiteAssignment = {
          ...assignment,
          siteId: editingSiteData.siteId,
          siteName: selectedSite ? selectedSite.siteName : assignment.siteName,
          siteCode: selectedSite ? selectedSite.siteCode : assignment.siteCode,
          role: editingSiteData.role,
          assignmentType: editingSiteData.assignmentType,
          effectiveFrom: editingSiteData.effectiveFrom,
          effectiveTo: editingSiteData.effectiveTo
        };
        
        // Mettre à jour aussi le parent
        setCurrentEmployee(prev => ({
          ...prev,
          multiSiteAssignments: (prev.multiSiteAssignments || []).map(a => 
            a.id === editingSiteId ? updatedAssignment : a
          )
        }));
        
        return updatedAssignment;
      }
      return assignment;
    }));

    // Réinitialiser l'édition
    setEditingSiteId(null);
    setEditingSiteData({
      siteId: 0,
      role: '',
      assignmentType: '',
      effectiveFrom: '',
      effectiveTo: ''
    });
  };

  const handleCancelSiteEdit = () => {
    setEditingSiteId(null);
    setEditingSiteData({
      siteId: 0,
      role: '',
      assignmentType: '',
      effectiveFrom: '',
      effectiveTo: ''
    });
  };

  const handleDeleteSite = (assignmentId: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette affectation multi-site ?')) {
      const pendingChange: PendingMultiSiteChange = {
        type: 'remove',
        id: assignmentId
      };

      // Notifier le parent avec le changement de suppression
      if (onMultiSiteChange) {
        onMultiSiteChange(pendingChange);
      }

      // Mettre à jour l'état local
      setMultiSiteAssignments(prev => prev.filter(a => a.id !== assignmentId));
      
      setCurrentEmployee(prev => ({
        ...prev,
        multiSiteAssignments: (prev.multiSiteAssignments || []).filter(a => a.id !== assignmentId)
      }));

    }
  };

  const handleCancelAdd = () => {
    setShowAddSite(false);
    setNewSiteData({
      siteId: 0,
      role: '',
      assignmentType: '',
      effectiveFrom: '',
      effectiveTo: ''
    });
  };

  return (
    <div className="space-y-4">
      {/* Liste des sites multi-sites */}
      <div className="space-y-2">
        {multiSiteAssignments.map((assignment) => (
          <div 
            key={assignment.id} 
            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
            onDoubleClick={() => handleEditSite(assignment.id)}
          >
            {editingSiteId === assignment.id ? (
              <div className="flex-1 space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Site
                    </label>
                    <select
                      value={editingSiteData.siteId}
                      onChange={(e) => setEditingSiteData(prev => ({ ...prev, siteId: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={0}>Sélectionner un site</option>
                      {sites.map(site => (
                        <option key={site.id} value={site.id}>
                          {site.siteName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rôle
                    </label>
                    <input
                      type="text"
                      value={editingSiteData.role}
                      onChange={(e) => setEditingSiteData(prev => ({ ...prev, role: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Rôle dans ce site"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type d'assignation
                    </label>
                    <select
                      value={editingSiteData.assignmentType}
                      onChange={(e) => setEditingSiteData(prev => ({ ...prev, assignmentType: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Sélectionner un type</option>
                      {assignmentTypes.map(type => (
                        <option key={type.code} value={type.code}>
                          {type.description}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date d'effet
                    </label>
                    <input
                      type="date"
                      value={editingSiteData.effectiveFrom}
                      onChange={(e) => setEditingSiteData(prev => ({ ...prev, effectiveFrom: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date de fin (optionnel)
                    </label>
                    <input
                      type="date"
                      value={editingSiteData.effectiveTo}
                      onChange={(e) => setEditingSiteData(prev => ({ ...prev, effectiveTo: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={handleCancelSiteEdit}
                    className="btn-secondary text-xs px-3 py-1"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSaveSiteEdit}
                    className="btn-primary text-xs px-3 py-1"
                  >
                    Sauvegarder
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-blue-600" />
                  <div>
                    <div className="font-medium text-gray-900">{assignment.siteName || 'Site inconnu'}</div>
                    <div className="text-sm text-gray-600">
                      {assignment.role} • {assignment.assignmentType} • {assignment.effectiveFrom}
                      {assignment.effectiveTo && ` - ${assignment.effectiveTo}`}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {editingSiteId !== assignment.id && (
              <div className="flex gap-2">
                <button
                  onClick={() => handleDeleteSite(assignment.id)}
                  className="p-1 text-red-600 hover:bg-red-100 rounded"
                  title="Supprimer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Bouton d'ajout */}
      {!showAddSite && (
        <button
          onClick={() => setShowAddSite(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Ajouter un site
        </button>
      )}

      {/* Formulaire d'ajout */}
      {showAddSite && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Nouveau site multi-site
          </h4>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Site *
                </label>
                <select
                  value={newSiteData.siteId}
                  onChange={(e) => setNewSiteData(prev => ({ ...prev, siteId: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={0}>Sélectionner un site</option>
                  {sites.map(site => (
                    <option key={site.id} value={site.id}>
                      {site.siteName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rôle *
                </label>
                <input
                  type="text"
                  value={newSiteData.role}
                  onChange={(e) => setNewSiteData(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Rôle dans ce site"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type d'assignation *
                </label>
                <select
                  value={newSiteData.assignmentType}
                  onChange={(e) => setNewSiteData(prev => ({ ...prev, assignmentType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Sélectionner un type</option>
                  {assignmentTypes.map(type => (
                    <option key={type.code} value={type.code}>
                      {type.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date d'effet *
                </label>
                <input
                  type="date"
                  value={newSiteData.effectiveFrom}
                  onChange={(e) => setNewSiteData(prev => ({ ...prev, effectiveFrom: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de fin (optionnel)
                </label>
                <input
                  type="date"
                  value={newSiteData.effectiveTo}
                  onChange={(e) => setNewSiteData(prev => ({ ...prev, effectiveTo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={handleCancelAdd}
                className="btn-secondary text-xs px-3 py-1"
              >
                Annuler
              </button>
              <button
                onClick={handleAddSite}
                className="btn-primary text-xs px-3 py-1"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Indicateur de changements en attente */}
      {pendingMultiSiteChanges.length > 0 && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-2 text-yellow-800">
            <Globe className="h-4 w-4" />
            <span className="font-medium">
              {pendingMultiSiteChanges.length} changement(s) multi-site en attente
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSiteManager;